import {
  Alert,
  Button,
  Group,
  Modal,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconAlertCircle } from "@tabler/icons-react";
import { useCallback } from "react";
import type { FormEvent } from "react";
import { useUpdateShippedCaseTracking } from "../../hooks/useWorkflows";
import type { ShippedCase } from "../../services/workflows.service";

interface ShippingTrackingModalProps {
  shippedCase: ShippedCase | null;
  canManage: boolean;
  onClose: () => void;
}

export default function ShippingTrackingModal({
  shippedCase,
  canManage,
  onClose,
}: ShippingTrackingModalProps) {
  const trackingMutation = useUpdateShippedCaseTracking();
  const form = useForm({
    initialValues: { trackingNumber: "" },
    validateInputOnBlur: true,
  });

  const handleOpened = useCallback(() => {
    form.setValues({
      trackingNumber: shippedCase?.shippingTrackingNumber ?? "",
    });
    form.clearErrors();
    trackingMutation.reset();
  }, [form, shippedCase, trackingMutation]);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!shippedCase || !canManage) return;
      const validation = form.validate();
      if (validation.hasErrors) return;
      try {
        await trackingMutation.mutateAsync({
          caseId: shippedCase.id,
          trackingNumber: form.values.trackingNumber,
        });
        onClose();
      } catch {
        // The accessible alert below provides the user-safe failure message.
      }
    },
    [canManage, form, onClose, shippedCase, trackingMutation]
  );

  return (
    <Modal
      opened={shippedCase !== null}
      onClose={onClose}
      onEnterTransitionEnd={handleOpened}
      title="Shipment details"
      size="md"
      centered
    >
      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          <Text size="sm">
            Case <strong>{shippedCase?.caseNumber}</strong>
          </Text>
          {trackingMutation.isError && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              title="Tracking number not saved"
              color="red"
              role="alert"
            >
              Please try again. If the problem continues, contact support.
            </Alert>
          )}
          <TextInput
            label="Tracking number"
            description="Optional"
            placeholder="Enter a tracking number"
            disabled={!canManage}
            {...form.getInputProps("trackingNumber")}
          />
          {!canManage && (
            <Text size="sm" c="dimmed">
              You have read-only access to shipping details.
            </Text>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              {canManage ? "Cancel" : "Close"}
            </Button>
            {canManage && (
              <Button type="submit" loading={trackingMutation.isPending}>
                Save
              </Button>
            )}
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
